import { useTranslation } from 'react-i18next';
import useSWR, { mutate } from 'swr';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import axios from 'axios';
import { Answers, Options, Errors } from './types/form';
import { optionsFetcher, useBasePath } from './utils/utilities';
import usePageView from './hooks/usePageView';

export default function Refinement() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [errors, setErrors] = useState<Errors>([]);
    const basePath = useBasePath();
    const [answers, setAnswers] = useState<Answers>(
        localStorage.getItem('answers')
            ? JSON.parse(localStorage.getItem('answers') || '{}')
            : []
    );
    const currentStorage: Answers = localStorage.getItem('answers')
        ? JSON.parse(localStorage.getItem('answers') || '{}')
        : [];

    const answerEnergySource = answers.energy_source;
    const { data, isLoading } = useSWR<Options>('/submission/', optionsFetcher);

    usePageView();

    if (!data || isLoading) return <div>Loading...</div>;

    const dhwPreperation = data.actions.POST.dhw_preparation;
    const customOccupants = data.actions.POST.custom_occupants;

    const uuid = localStorage.getItem('uuid');

    async function handleSubmit(e: React.MouseEvent<HTMLElement>) {
        e.preventDefault();
        try {
            const response = await axios.patch(`/submission/${uuid}/`, answers);
            if (response.data.identifier) {
                localStorage.setItem('answers', JSON.stringify(response.data));
            }
            await mutate(`/submission/${uuid}/recommendations`);
            navigate(`${basePath}/results`);
        } catch (error: any) {
            if (
                error?.response?.data &&
                typeof error?.response?.data === 'object'
            ) {
                const responseErrors = Object.entries(error.response?.data).map(
                    ([key, value]) => {
                        return `${key}: ${value}`;
                    }
                );
                setErrors(responseErrors);
            } else {
                setErrors([t('Something went wrong')]);
            }
        }
    }

    return (
        <form className="lead-form">
            <div className="form-refinement">
                <fieldset>
                    <legend>{t('Refinement')}</legend>

                    <div className="form-option__heading">
                        <h2 className="heading-beta form-option__title">
                            {t('Refinement')}
                        </h2>
                    </div>

                    {answerEnergySource !== 'other' ? (
                        <>
                            <div
                                id="custom_annual_energy_consumption"
                                className="form-question form-question--refinement"
                            >
                                <div className="inputgroup">
                                    <label
                                        htmlFor="custom_energy_consumption"
                                        className="form-option__refinement-title"
                                    >
                                        {t('Yearly consumption in ')}
                                        {answers.consumption_unit}
                                    </label>
                                    <div className="input-containing-unit">
                                        <span className="input-unit input-unit--post">
                                            {t('kWh')}
                                        </span>
                                        <input
                                            id="custom_energy_consumption"
                                            className="input--nomargin"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="10000000"
                                            name="custom_annual_energy_consumption"
                                            onChange={(e) => {
                                                setAnswers((prev) => ({
                                                    ...prev,
                                                    custom_annual_energy_consumption:
                                                        e.target.value,
                                                }));
                                            }}
                                            defaultValue={
                                                answers.custom_annual_energy_consumption
                                                    ? answers.custom_annual_energy_consumption
                                                    : answers.annual_energy_consumption
                                            }
                                        />
                                    </div>
                                    <p className="form-option__hint form-option__hint--refinement">
                                        {t('currently')}:{' '}
                                        {(currentStorage.custom_annual_energy_consumption ||
                                            currentStorage.annual_energy_consumption) &&
                                            t('intlNumber', {
                                                val: currentStorage.custom_annual_energy_consumption
                                                    ? currentStorage.custom_annual_energy_consumption
                                                    : currentStorage.annual_energy_consumption,
                                            })}
                                    </p>
                                </div>
                            </div>
                            <div
                                id="dhw_preparation"
                                className="form-question form-question--refinement"
                            >
                                <div className="form-option__refinement-title">
                                    {dhwPreperation.label}
                                </div>

                                <ul className="dhw-refinement">
                                    <li className="form-option form-option--refinement">
                                        <input
                                            className="required form-option__input"
                                            id="dhw_preparation1"
                                            name="dhw_preparation"
                                            value="1"
                                            type="radio"
                                            onChange={() => {
                                                setAnswers((prev) => ({
                                                    ...prev,
                                                    dhw_preparation: true,
                                                }));
                                            }}
                                            checked={
                                                answers.dhw_preparation === true
                                            }
                                        />
                                        <label
                                            className="form-option__label"
                                            htmlFor="dhw_preparation1"
                                        >
                                            <div className="form-option__text form-option__text--refinement">
                                                {t('Yes')}
                                            </div>
                                        </label>
                                    </li>

                                    <li className="form-option form-option--refinement">
                                        <input
                                            className="required form-option__input"
                                            id="dhw_preparation2"
                                            name="dhw_preparation"
                                            value=""
                                            type="radio"
                                            onChange={() => {
                                                setAnswers((prev) => ({
                                                    ...prev,
                                                    dhw_preparation: false,
                                                }));
                                            }}
                                            checked={
                                                answers.dhw_preparation ===
                                                false
                                            }
                                        />
                                        <label
                                            className="form-option__label"
                                            htmlFor="dhw_preparation2"
                                        >
                                            <div className="form-option__text form-option__text--refinement">
                                                {t('No')}
                                            </div>
                                        </label>
                                    </li>
                                </ul>
                            </div>
                        </>
                    ) : (
                        <input type="hidden" name="dhw_preparation" value="1" />
                    )}

                    <div
                        id="custom_occupants"
                        className="form-question form-question--refinement"
                    >
                        <div className="inputgroup">
                            <label
                                htmlFor="custom_num_occupants"
                                className="form-option__refinement-title"
                            >
                                {customOccupants.label}
                            </label>
                            <div className="input-containing-unit">
                                <span className="input-unit input-unit--post">
                                    {t('People')}
                                </span>
                                <input
                                    id="custom_num_occupants"
                                    className="input--nomargin required"
                                    type="number"
                                    name="custom_occupants"
                                    onChange={(e) => {
                                        setAnswers((prev) => ({
                                            ...prev,
                                            custom_occupants: e.target.value,
                                        }));
                                    }}
                                    defaultValue={
                                        answers.custom_occupants
                                            ? answers.custom_occupants
                                            : answers.occupants
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    {answerEnergySource !== 'other' && (
                        <div className="form-question form-question--refinement">
                            <div className="inputgroup">
                                <label
                                    htmlFor="gas_cost"
                                    className="form-option__refinement-title"
                                >
                                    {t('Adjustment of price per ')}
                                    {answers.consumption_unit}
                                </label>
                                <div className="input-containing-unit">
                                    <span className="input-unit input-unit--post">
                                        CHF
                                    </span>
                                    <input
                                        id="gas_cost"
                                        className="required"
                                        type="number"
                                        step="0.001"
                                        min="0"
                                        max="10000000"
                                        name={answers.adjustment_price}
                                        defaultValue={
                                            answers.adjustment_price_value
                                        }
                                        onChange={(e) => {
                                            setAnswers((prev) => ({
                                                ...prev,
                                                [answers.adjustment_price]:
                                                    e.target.value,
                                            }));
                                        }}
                                    />
                                </div>
                                <p className="form-option__hint form-option__hint--refinement">
                                    {t('currently')}: CHF{' '}
                                    {currentStorage.adjustment_price_value &&
                                        t('intlNumber', {
                                            val: currentStorage.adjustment_price_value,
                                        })}
                                </p>
                            </div>
                        </div>
                    )}
                </fieldset>
            </div>

            {errors && errors.length > 0 && (
                <div className="errors">
                    <div className="errors__refinement">
                        {errors.map((error) => {
                            return <div key={error}>{error}</div>;
                        })}
                    </div>
                </div>
            )}

            <fieldset>
                <legend>{t('Navigation')}</legend>
                <div className="form-navigation form-navigation--centered">
                    <button
                        className="form-navigation__button form-navigation__button--next form-navigation__button--submit"
                        type="submit"
                        onClick={(e) => handleSubmit(e)}
                    >
                        {t('My recommendation')}
                    </button>
                </div>
            </fieldset>
        </form>
    );
}
